import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Paperclip, Smile, Loader2, FileIcon, ClipboardIcon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import AttachmentPreview from './AttachmentPreview';
import omnichannelService from '../../services/omnichannelService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';

interface MessageComposerProps {
  conversationId: string;
  onSendMessage: (content: string, attachments: File[]) => Promise<void>;
  isInternalNote?: boolean;
  onToggleInternalNote?: (value: boolean) => void;
  onOpenFormTemplateSelector?: () => void;
}

export default function MessageComposer({
  conversationId,
  onSendMessage,
  isInternalNote = false,
  onToggleInternalNote,
  onOpenFormTemplateSelector
}: MessageComposerProps) {
  const { theme } = useTheme();
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingTimeRef = useRef<number>(0);

  // Send typing indicator (throttled)
  const sendTypingIndicator = useCallback(async () => {
    if (isInternalNote) return; // Don't send typing for internal notes

    const now = Date.now();
    const timeSinceLastTyping = now - lastTypingTimeRef.current;

    // Only send typing indicator every 3 seconds
    if (timeSinceLastTyping < 3000) return;

    try {
      lastTypingTimeRef.current = now;
      await omnichannelService.sendTypingIndicator(conversationId);
    } catch (error) {
      console.error('Failed to send typing indicator:', error);
    }
  }, [conversationId, isInternalNote]);

  // Handle message change with typing indicator
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target.value;
    setMessage(newMessage);

    // Send typing indicator when user starts typing
    if (newMessage.length > 0) {
      sendTypingIndicator();

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = message;
    const before = text.substring(0, start);
    const after = text.substring(end);

    setMessage(before + emojiData.emoji + after);
    setShowEmojiPicker(false);

    // Set cursor position after emoji
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + emojiData.emoji.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Validate file size (25MB limit for Messenger)
    const maxSize = 25 * 1024 * 1024;
    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 25MB.`);
        return false;
      }
      return true;
    });

    setAttachments([...attachments, ...validFiles]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((!message.trim() && attachments.length === 0) || sending) return;

    try {
      setSending(true);
      await onSendMessage(message, attachments);
      setMessage('');
      setAttachments([]);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  return (
    <div
      className={`border-t p-4 ${
        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      }`}
    >
      <form onSubmit={handleSend} className="space-y-3">
        {/* Attachment Previews */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachments.map((file, index) => (
              <AttachmentPreview
                key={index}
                attachment={{
                  file_name: file.name,
                  file_type: file.type,
                  file_size: file.size,
                  mime_type: file.type
                }}
                onRemove={() => handleRemoveAttachment(index)}
                showRemove={true}
              />
            ))}
          </div>
        )}

        {/* Message Input Area */}
        <div className="flex items-start gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleMessageChange}
              onKeyDown={handleKeyDown}
              placeholder={isInternalNote ? 'Add an internal note...' : 'Type your message...'}
              rows={5}
              className={`w-full px-3 py-2 rounded-lg border resize-none ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              disabled={sending}
            />

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div className="absolute bottom-full mb-2 left-0 z-50">
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  theme={theme === 'dark' ? Theme.DARK : Theme.LIGHT}
                  searchPlaceHolder="Search emoji..."
                  width={350}
                  height={400}
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col items-end gap-2">
            {/* Emoji Button */}
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className={`p-2 rounded-lg transition-colors ${
                theme === 'dark'
                  ? 'hover:bg-gray-700 text-gray-300'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
              title="Add emoji"
              disabled={sending}
            >
              <Smile className="h-5 w-5" />
            </button>

            {/* File Attachment Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={`p-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  title="Attach file"
                  disabled={sending}
                >
                  <Paperclip className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                  <FileIcon className="h-5 w-5" />
                  <span>Attach a file</span>
                </DropdownMenuItem>
                {onOpenFormTemplateSelector && (
                  <DropdownMenuItem onClick={onOpenFormTemplateSelector}>
                    <ClipboardIcon className="h-5 w-5" />
                    <span>Send form</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleFileSelect}
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
            />

            {/* Send Button */}
            <button
              type="submit"
              disabled={(!message.trim() && attachments.length === 0) || sending}
              className={`p-2 rounded-lg flex items-center gap-2 transition-colors ${
                (!message.trim() && attachments.length === 0) || sending
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              } ${
                isInternalNote
                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex items-center justify-between">
          {/* Hint Text */}
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Press Enter to send, Shift+Enter for new line. Max file size: 25MB
          </p>
          {/* Internal Note Toggle */}
          {onToggleInternalNote && (
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={isInternalNote}
                  onChange={(e) => onToggleInternalNote(e.target.checked)}
                  className="rounded"
                />
                <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                  Internal Note
                </span>
              </label>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
